import React from 'react';
import Flatpickr from 'react-flatpickr';
import Skeleton from 'react-loading-skeleton';
import HospitalSelector from './shared/HospitalSelector';
import AppointmentVolumeChart from './AppointmentVolumeChart';
import AppointmentStatusPieChart from './AppointmentStatusPieChart';
import AppointmentSourceChart from './AppointmentSourceChart';
import AppointmentTopHospitalsChart from './AppointmentTopHospitalsChart';
import AppointmentBusyHoursMatrix from './AppointmentBusyHoursMatrix';
import AppointmentYearHeatmap from './AppointmentYearHeatmap';
import { formatNumber, interpolate, isoDate } from './shared/chartFormat';

const MS_PER_DAY = 86400000;

// Gradient modifiers reuse the existing .dash-stat-card set.
const KPI_CARDS = [
  { key: 'total', type: 'appointment', modifier: 'appointments', icon: 'zmdi-calendar-check', titleKey: 'kpi_total' },
  { key: 'completed', type: 'appointment_completed', modifier: 'registrations', icon: 'zmdi-check-circle', titleKey: 'kpi_completed' },
  { key: 'waiting', type: 'appointment_waiting', modifier: 'feedbacks', icon: 'zmdi-time', titleKey: 'kpi_waiting' },
  { key: 'canceled', type: 'appointment_canceled', modifier: 'participants-cancelled', icon: 'zmdi-close-circle', titleKey: 'kpi_canceled' }
];

const PRESET_DAYS = { 30: 30, 90: 90 };

const emptyKpi = () => ({ total: null, change: null, changeLabel: null, loading: true });

// PE-438: the appointment statistics dashboard. One filter state — range,
// hospital, date basis — governs every KPI and chart below it.
export default class AppointmentStatisticsPage extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 29);

    this.state = {
      hospitals: [],
      hospitalId: '',
      dateField: 'created_at',
      start: isoDate(start),
      end: isoDate(today),
      preset: '30',
      kpis: { total: emptyKpi(), completed: emptyKpi(), waiting: emptyKpi(), canceled: emptyKpi() }
    };
  }

  componentDidMount() {
    if (!this.props.hospital_id) {
      // HospitalSelector filters client-side, so it wants the whole list once.
      fetch('/api/search/hospitals')
        .then(response => (response.ok ? response.json() : []))
        .then(hospitals => {
          if (this.unmounted) return;
          this.setState({ hospitals: Array.isArray(hospitals) ? hospitals : [] });
        })
        .catch(() => {});
    }
    this.fetchKpis();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  // "Y-m-d to Y-m-d" range + hospital + date basis, the query every endpoint
  // shares. busy_chart pins the basis to created_at (the date column holds
  // midnight timestamps — grouping it by hour would blank the matrix) and
  // calendar_chart pins its own year window server-side, so it gets no date.
  buildQuery(options) {
    const opts = options || {};
    const params = new URLSearchParams();
    if (opts.withDate !== false) params.set('date', `${this.state.start} to ${this.state.end}`);
    const hospitalId = this.props.hospital_id || this.state.hospitalId;
    if (hospitalId) params.set('hospital_id', hospitalId);
    params.set('date_field', opts.dateField || this.state.dateField);
    return `?${params.toString()}`;
  }

  applyFilters(partial) {
    this.setState(partial, () => this.fetchKpis());
  }

  applyPreset(key) {
    const today = new Date();
    let start;
    if (PRESET_DAYS[key]) {
      start = new Date(today);
      start.setDate(start.getDate() - (PRESET_DAYS[key] - 1));
    } else {
      start = new Date(today.getFullYear(), 0, 1);
    }
    this.applyFilters({ preset: key, start: isoDate(start), end: isoDate(today) });
  }

  fetchKpis() {
    const requestId = (this.kpiRequestId = (this.kpiRequestId || 0) + 1);
    const query = this.buildQuery();

    this.setState({ kpis: { total: emptyKpi(), completed: emptyKpi(), waiting: emptyKpi(), canceled: emptyKpi() } });
    KPI_CARDS.forEach(card => {
      fetch(`/api/dashboard/statistics/${card.type}.json${query}`)
        .then(res => (res.ok ? res.json() : Promise.reject(res)))
        .then(json => {
          // The endpoint formats total as a delimited string ("57,412").
          const total = Number(String(json.total).replace(/,/g, ''));
          this.storeKpi(requestId, card.key, {
            total: Number.isFinite(total) ? total : null,
            change: json.change,
            changeLabel: json.change_label,
            loading: false
          });
        })
        .catch(() => this.storeKpi(requestId, card.key, { total: null, change: null, changeLabel: null, loading: false }));
    });
  }

  storeKpi(requestId, key, value) {
    if (this.unmounted || requestId !== this.kpiRequestId) return;
    this.setState(prev => ({ kpis: { ...prev.kpis, [key]: value } }));
  }

  rangeDays() {
    const { start, end } = this.state;
    const span = (new Date(`${end}T00:00:00`) - new Date(`${start}T00:00:00`)) / MS_PER_DAY;
    return Math.max(1, Math.round(span) + 1);
  }

  kpiCaption(key) {
    const { labels, locale } = this.props;
    const { kpis } = this.state;
    const card = kpis[key];
    if (card.loading || card.total === null) return null;

    if (key === 'total') {
      const value = formatNumber(Math.round(card.total / this.rangeDays()), locale);
      return interpolate(labels.kpi_avg_per_day, { value });
    }

    // Share of the total card, computed from the two responses already here.
    if (kpis.total.loading || !kpis.total.total) return null;
    const share = (card.total / kpis.total.total) * 100;
    return interpolate(labels.kpi_share_of_total, {
      value: formatNumber(share, locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    });
  }

  renderChange(card) {
    const { labels, locale } = this.props;
    if (card.change === null || card.change === undefined || !card.changeLabel) return null;
    const num = Number(card.change);
    if (!Number.isFinite(num)) return null;

    const icon = num > 0 ? 'zmdi-trending-up' : num < 0 ? 'zmdi-trending-down' : 'zmdi-trending-flat';
    const sign = num > 0 ? '+' : '';
    const label = card.changeLabel === 'vs_previous_period' ? labels.vs_previous_period
      : card.changeLabel === 'vs_yesterday' ? labels.vs_yesterday : '';

    return (
      <span className="dash-stat-card__change">
        <i className={`zmdi ${icon}`} /> {sign}{formatNumber(num, locale)} {label}
      </span>
    );
  }

  renderFilterBar() {
    const { labels, date_field_options: dateFieldOptions, hospital_id: lockedHospital } = this.props;
    const { hospitals, hospitalId, dateField, start, end, preset } = this.state;
    const presets = [
      { key: '30', label: labels.preset_thirty },
      { key: '90', label: labels.preset_ninety },
      { key: 'year', label: labels.preset_year }
    ];

    return (
      <div className="card appt-stats__filter-card">
        <div className="card-body appt-stats__filter-bar">
          <div className="appt-stats__control appt-stats__control--range form-group">
            <i className="zmdi zmdi-calendar-note appt-stats__control-icon" aria-hidden="true" />
            <Flatpickr
              value={[start, end]}
              options={{ mode: 'range', dateFormat: 'Y-m-d', altInput: true, altFormat: 'j M Y', disableMobile: true }}
              onChange={dates => {
                if (dates.length !== 2) return;
                this.applyFilters({ preset: null, start: isoDate(dates[0]), end: isoDate(dates[1]) });
              }}
              className="form-control"
              placeholder={labels.date_range_placeholder}
            />
            <i className="form-group__bar" />
          </div>

          {/* A branch admin is already scoped server-side, so the picker would
              be a control that cannot change anything. */}
          {!lockedHospital && (
            <div className="appt-stats__control appt-stats__control--hospital">
              <HospitalSelector
                hospitals={hospitals}
                selectedId={hospitalId}
                onSelect={id => this.applyFilters({ hospitalId: id })}
                onClear={() => this.applyFilters({ hospitalId: '' })}
                translations={labels}
              />
            </div>
          )}

          <div className="appt-stats__control appt-stats__control--basis">
            <select
              className="form-control"
              aria-label={labels.basis}
              value={dateField}
              onChange={event => this.applyFilters({ dateField: event.target.value })}
            >
              {(dateFieldOptions || []).map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="appt-stats__spacer" />

          <div className="appt-stats__pills" role="group" aria-label={labels.date_range_placeholder}>
            {presets.map(item => (
              <button
                key={item.key}
                type="button"
                className={`btn btn-sm rounded-pill ${preset === item.key ? 'btn-primary' : 'btn-outline-primary'} appt-stats__pill`}
                aria-pressed={preset === item.key}
                onClick={() => this.applyPreset(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderKpis() {
    const { labels } = this.props;
    const { kpis } = this.state;

    return (
      <div className="row dash-stat-cards">
        {KPI_CARDS.map(card => {
          const data = kpis[card.key];
          const caption = this.kpiCaption(card.key);

          return (
            <div className="col-sm-6 col-xl-3" key={card.key}>
              <div className={`dash-stat-card dash-stat-card--${card.modifier}`}>
                <div className="dash-stat-card__icon">
                  <i className={`zmdi ${card.icon}`} />
                </div>
                <div className="dash-stat-card__info">
                  {data.loading ? (
                    <h2><Skeleton height={20} width={30} /></h2>
                  ) : (
                    <div className="d-flex align-items-baseline">
                      <h2>{formatNumber(data.total, this.props.locale)}</h2>
                      {this.renderChange(data)}
                    </div>
                  )}
                  <small>{labels[card.titleKey]}</small>
                  {caption && <p className="dash-stat-card__caption">{caption}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { labels, locale } = this.props;
    const { dateField } = this.state;
    const chartsQuery = this.buildQuery();
    const busyQuery = this.buildQuery({ dateField: 'created_at' });
    const yearQuery = this.buildQuery({ withDate: false });
    // Bulanan's line_chart honors only the hospital filter — range and basis
    // stay out so its URL matches the endpoint's actual contract.
    const hospitalId = this.props.hospital_id || this.state.hospitalId;
    const monthlyQuery = hospitalId ? `?hospital_id=${encodeURIComponent(hospitalId)}` : '';

    return (
      <div className="appt-stats">
        {this.renderFilterBar()}
        {this.renderKpis()}

        <div className="card">
          <div className="card-body">
            <AppointmentVolumeChart query={chartsQuery} monthlyQuery={monthlyQuery} labels={labels} locale={locale} dateField={dateField} />
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-6 col-xl-4 d-flex">
            <div className="card appt-stats__chart-card">
              <div className="card-body">
                <h4 className="card-title mb-3">{labels.status_title}</h4>
                <AppointmentStatusPieChart query={chartsQuery} />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4 d-flex">
            <div className="card appt-stats__chart-card">
              <div className="card-body">
                <h4 className="card-title mb-3">{labels.source_title}</h4>
                <AppointmentSourceChart query={chartsQuery} />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4 d-flex">
            <div className="card appt-stats__chart-card">
              <div className="card-body">
                <h4 className="card-title mb-3">{labels.hospitals_title}</h4>
                <AppointmentTopHospitalsChart query={chartsQuery} labels={labels} locale={locale} />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <AppointmentBusyHoursMatrix query={busyQuery} labels={labels} locale={locale} />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <AppointmentYearHeatmap query={yearQuery} labels={labels} locale={locale} />
          </div>
        </div>
      </div>
    );
  }
}
